import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ISelectOption } from '@widgets/multi-select';
import { ACCOUNT_TYPE, IAccount } from '@entities/account/IAccount';
import BaseAccountPopupEdit from '@pages/Account/Edit/components/BaseAccountPopupEdit';
import Label from '@shared/uikit/label';
import SingleSelectForm from '@shared/form/SingleSelectForm';
import IconCross from '@shared/icons/IconCross';
import MiningAccountTypeEdit from '@pages/Account/Edit/Type/Mining';
import ReferralAccountTypeEdit from '@pages/Account/Edit/Type/Referral';
import { resolveAccountTypeTitle } from '@pages/Account/helpers/accountTypeHelper';
import useAccountEditOptions from '@pages/Account/hooks/useAccountEditOptions';

interface IProps {
  account: IAccount;
  onClose: () => void;
  onSubmit: (data: IChangeAccountTypeValues) => Promise<void>;
}

export interface IChangeAccountTypeValues {
  accountType: ISelectOption | null;
  discount?: number | null;
  agents?: { account: ISelectOption | null; cashback: number }[] | null;
}

const ChangeAccountTypePopup: FC<IProps> = ({ account, onClose, onSubmit }) => {
  const optionsFetcher = useAccountEditOptions();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting },
  } = useForm<IChangeAccountTypeValues>({
    defaultValues: {
      accountType: account.account_type
        ? {
            value: account.account_type,
            label: resolveAccountTypeTitle(account.account_type),
          }
        : null,
      discount:
        account.account_type === ACCOUNT_TYPE.REFERRAL
          ? account.discount
          : account.mining_terms?.discount || 0,
      agents:
        account.account_type === ACCOUNT_TYPE.REFERRAL
          ? account.agents.map(agent => ({
              account: { value: agent.account, label: agent.account },
              cashback: agent.cashback,
            }))
          : [],
    },
  });

  const accountType = watch('accountType');

  return (
    <BaseAccountPopupEdit
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={optionsFetcher.isLoading}
      isSubmitting={isSubmitting}
      account={account}
    >
      <div className="border-b border-solid pb-[15px] border-opacity-10 border-[#959595] relative">
        <input type="hidden" />
        <div className="flex items-center">
          <span className="text-[16px] text-[#FFFFFF]">Назначить тип аккаунта</span>
        </div>
        <div className="absolute right-0 top-[10px] cursor-pointer" onClick={onClose}>
          <IconCross />
        </div>
      </div>

      <div className="grid gap-3 py-[20px] border-b border-solid border-opacity-10 border-[#959595] max-h-[500px] overflow-y-scroll overflow-x-hidden">
        <div className="grid grid-cols-[1fr_1fr] justify-items-start items-center">
          <Controller
            name="accountType"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Label title="Тип аккаунта" color="white" />
                <SingleSelectForm
                  placeholder="Выберите тип"
                  isLoading={optionsFetcher.isLoading}
                  options={optionsFetcher.accountTypeOptions}
                  width="270px"
                  onChange={(val: ISelectOption | null) => field.onChange(val)}
                  value={field.value}
                  state={fieldState}
                />
              </>
            )}
            rules={{ required: true }}
          />
        </div>

        {accountType?.value === ACCOUNT_TYPE.MINING ? (
          <MiningAccountTypeEdit account={account} control={control} />
        ) : null}
        {accountType?.value === ACCOUNT_TYPE.REFERRAL ? (
          <ReferralAccountTypeEdit account={account} control={control} options={optionsFetcher} />
        ) : null}
      </div>
    </BaseAccountPopupEdit>
  );
};

export default ChangeAccountTypePopup;
